package link.erpango.quip.domain.noteimport

import kotlinx.datetime.LocalDate
import link.erpango.quip.domain.extraction.CandidateMaintenance
import link.erpango.quip.domain.extraction.CandidateSparePart
import link.erpango.quip.domain.extraction.CandidateVehicle
import link.erpango.quip.domain.extraction.ExtractedField
import link.erpango.quip.domain.extraction.ExtractionConfidence
import link.erpango.quip.domain.extraction.ExtractionResult

/**
 * Maps the offline parser result into the review model. Values are canonicalized for the
 * forms (catalog brand spelling, dropdown options, UTC-midnight dates) and keep their note
 * span so the review UI can quote where each one came from.
 *
 * Ids are "v0", "m0", "p0"... in parser order.
 */
fun ExtractionResult.toImportDraft(ctx: DraftContext): ImportDraft {
    val vehicleDrafts = vehicles.mapIndexed { index, vehicle -> vehicle.toDraft("v$index", ctx) }
    fun refFor(index: Int?): VehicleRef =
        index?.let { vehicleDrafts.getOrNull(it) }?.let { VehicleRef.Draft(it.id) } ?: VehicleRef.Unassigned

    return ImportDraft(
        vehicles = vehicleDrafts,
        maintenances = maintenances.mapIndexed { index, m -> m.toDraft("m$index", refFor(m.vehicleIndex), ctx) },
        spareParts = spareParts.mapIndexed { index, p -> p.toDraft("p$index", refFor(p.vehicleIndex), ctx) },
    )
}

private fun <T> ExtractedField<T>.suggest(): SuggestedField<T> =
    SuggestedField(value = value, confidence = confidence, source = FieldSource.NOTE, span = span)

private fun <T, R> ExtractedField<T>.suggestMapped(
    maxConfidence: ExtractionConfidence = ExtractionConfidence.HIGH,
    transform: (T) -> R?,
): SuggestedField<R>? {
    val mapped = transform(value) ?: return null
    return SuggestedField(mapped, confidence.atMost(maxConfidence), FieldSource.NOTE, span)
}

/** A date as written ("15/03/2019") normalized to UTC-midnight epoch millis, or null. */
private fun ExtractedField<String>.suggestDate(today: LocalDate): SuggestedField<Long>? {
    val normalized = normalizeNoteDate(value, today) ?: return null
    return SuggestedField(
        value = normalized.date.toUtcMidnightMillis(),
        confidence = confidence.atMost(normalized.confidence),
        source = FieldSource.NOTE,
        span = span,
    )
}

private fun CandidateVehicle.toDraft(id: String, ctx: DraftContext): VehicleDraft {
    val tire = tireSize?.suggestMapped(ExtractionConfidence.MEDIUM) { normalizeTireSize(it) }
    return VehicleDraft(
        id = id,
        vin = vin?.suggestMapped { normalizeVin(it) },
        plate = plate?.suggestMapped { normalizePlate(it) },
        brand = brand?.suggestMapped { canonicalName(it, ctx.brands) ?: it },
        model = model?.suggest(),
        year = year?.suggest(),
        vehicleType = vehicleType?.suggestMapped { vehicleTypeFromCode(it) },
        engineOil = oilSpec?.suggestMapped { canonicalOilViscosity(it) ?: it },
        // A note rarely says whether a size is front or rear: suggest it for both.
        frontTireSize = tire,
        rearTireSize = tire,
        recommendedFuel = fuelType?.let { field ->
            fuelOptionFromText(field.value)?.let { fuel ->
                SuggestedField(fuel.option, field.confidence.atMost(fuel.confidence), FieldSource.NOTE, field.span)
            }
        },
        engineDetails = engineDetails?.suggest(),
        cylinderCount = cylinderCount?.suggest(),
        frontBulbType = frontBulbType?.suggest(),
        rearBulbType = rearBulbType?.suggest(),
        batterySpecs = batterySpecs?.suggest(),
        coolantType = coolantType?.suggest(),
        frontTirePressureBelow160 = frontTirePressureBelow160?.suggest(),
        rearTirePressureBelow160 = rearTirePressureBelow160?.suggest(),
        frontTirePressureAbove160 = frontTirePressureAbove160?.suggest(),
        rearTirePressureAbove160 = rearTirePressureAbove160?.suggest(),
        firstRegistrationDate = firstRegistrationDate?.suggestDate(ctx.today),
        nextItvDate = nextItvDate?.suggestDate(ctx.today),
    )
}

private fun CandidateMaintenance.toDraft(id: String, vehicle: VehicleRef, ctx: DraftContext): MaintenanceDraft {
    val normalized = date?.let { normalizeNoteDate(it.value, ctx.today) }
    return MaintenanceDraft(
        id = id,
        description = description.suggestMapped { it.trim().capitalizeFirst() },
        date = normalized?.let {
            SuggestedField(
                value = it.date.toUtcMidnightMillis(),
                confidence = date.confidence.atMost(it.confidence),
                source = FieldSource.NOTE,
                span = date.span,
            )
        },
        datePrecision = normalized?.precision,
        rawDate = date?.value,
        kilometers = kilometers?.suggest(),
        cost = cost?.suggest(),
        workshop = workshop?.suggest(),
        mechanic = mechanic?.suggest(),
        vehicle = vehicle,
    )
}

private fun CandidateSparePart.toDraft(id: String, vehicle: VehicleRef, ctx: DraftContext): SparePartDraft {
    val typeField = type?.takeIf { it.value in ctx.sparePartCatalog.sparePartTypes }
    val catalogType = typeField?.let { ctx.sparePartCatalog.sparePartTypes.getValue(it.value) }
    val categoryId = catalogType?.categoryId
        ?: category?.value?.takeIf { it in ctx.sparePartCatalog.categories }
    return SparePartDraft(
        id = id,
        phrase = description.value,
        categoryId = categoryId?.let {
            SuggestedField(it, (category ?: description).confidence, FieldSource.NOTE, description.span)
        },
        typeId = typeField?.suggest(),
        brand = brand?.suggest(),
        reference = reference?.suggest(),
        // Invoice lines state the quantity ("4 L"); without it a type with quantity (oil, brake
        // fluid) cannot be imported (see toDSparePart). The catalog unit is only a fallback.
        quantity = quantity?.suggest(),
        unit = unit?.suggest()
            ?: catalogType?.unit?.let { SuggestedField(it, ExtractionConfidence.MEDIUM, FieldSource.NOTE) },
        vehicle = vehicle,
    )
}
